import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { VatCountryCode } from '~/api/common/types';

import { europeanUnionVatCountryCodes, vatCountryCodes } from '~/constants/countries';
import { useIntl } from '~/hooks/useIntl';
import { includes } from '~/utils/arrays';

import { mapVatCountryCodeToCountryCode } from '../utils';

export type VatCountryCodeGroup = { items: VatCountryCode[]; label: string };

/**
 * The VAT country codes as a European Union group and an international group, each sorted by their localised
 * country name. Shaped for the `items` prop of a grouped combobox.
 *
 * Splits on European Union membership, not geography: only a member state has an intra-community VAT number.
 */
export const useVatCountryCodeGroups = (): VatCountryCodeGroup[] => {
  const { displayNameOfCountry } = useIntl();
  const { t } = useTranslation(['clients']);

  return useMemo(() => {
    const getCountryName = (countryCode: VatCountryCode) => displayNameOfCountry(mapVatCountryCodeToCountryCode(countryCode));

    const sortByCountryName = (countryCodes: VatCountryCode[]) =>
      countryCodes.toSorted((a, b) => getCountryName(a).localeCompare(getCountryName(b)));

    return [
      {
        items: sortByCountryName(vatCountryCodes.filter((countryCode) => includes(europeanUnionVatCountryCodes, countryCode))),
        label: t('clients:fields.countryCode.groups.europeanUnion'),
      },
      {
        items: sortByCountryName(vatCountryCodes.filter((countryCode) => !includes(europeanUnionVatCountryCodes, countryCode))),
        label: t('clients:fields.countryCode.groups.international'),
      },
    ];
  }, [displayNameOfCountry, t]);
};
